import React, {createContext, useContext, useReducer, useEffect, useCallback} from 'react';
import {authAPI, userAPI} from '../services/api';
import {setAuthToken, setUser, getUser, getAuthToken, clearAuth, isTokenExpired} from '../utils/auth';

const AuthContext = createContext();

const authReducer = (state, action) => {
    switch (action.type) {
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload
            };
        case 'LOGIN_START':
        case 'REGISTER_START':
            return {
                ...state,
                loading: true,
                error: null
            };
        case 'LOGIN_SUCCESS':
        case 'REGISTER_SUCCESS':
            return {
                ...state,
                loading: false,
                isAuthenticated: true,
                user: action.payload.user,
                token: action.payload.token,
                error: null
            };
        case 'LOGIN_FAIL':
        case 'REGISTER_FAIL':
            return {
                ...state,
                loading: false,
                isAuthenticated: false,
                user: null,
                token: null,
                error: action.payload
            };
        case 'LOGOUT':
            return {
                ...state,
                loading: false,
                appLoading: false,
                isAuthenticated: false,
                user: null,
                token: null,
                error: null
            };
        case 'LOAD_USER_SUCCESS':
            return {
                ...state,
                loading: false,
                appLoading: false,
                isAuthenticated: true,
                user: action.payload
            };
        case 'LOAD_USER_FAIL':
            return {
                ...state,
                loading: false,
                appLoading: false,
                isAuthenticated: false,
                user: null,
                token: null
            };
        case 'UPDATE_USER':
            return { ...state, user: { ...state.user, ...action.payload } };
        case 'CLEAR_ERROR':
            return {
                ...state,
                error: null
            };
        case 'SET_APP_LOADING':
            return {
                ...state,
                appLoading: action.payload
            };
        default:
            return state;
    }
};

// État initial basé sur ce qui est en localStorage
const getInitialState = () => {
    const token = getAuthToken();
    const user = getUser();

    return {
        loading: true, // Utilisé pour les actions (login/register/...)
        appLoading: true, // Bootstrapping initial de l'app (vérification du token)
        isAuthenticated: false, // Sera déterminé après vérification
        user: user,
        token: token,
        error: null
    };
};

export const AuthProvider = ({children}) => {
    const [state, dispatch] = useReducer(authReducer, getInitialState());

    // Déconnexion
    const logout = useCallback(async () => {
        dispatch({type: 'SET_LOADING', payload: true});

        try {
            await authAPI.logout();
        } catch (error) {
            console.error('Erreur lors de la déconnexion:', error);
        } finally {
            clearAuth();
            dispatch({type: 'LOGOUT'});
        }
    }, []);

    // Charger les données utilisateur
    const loadUser = useCallback(async () => {
        const token = getAuthToken();

        if (!token) {
            dispatch({type: 'LOAD_USER_FAIL'});
            return;
        }

        if (isTokenExpired(token)) {
            clearAuth();
            dispatch({type: 'LOAD_USER_FAIL'});
            return;
        }

        const cachedUser = getUser();
        if (cachedUser) {
            dispatch({
                type: 'LOAD_USER_SUCCESS',
                payload: cachedUser
            });
        }

        try {
            const response = await authAPI.getMe();

            // Mettre à jour les données utilisateur en localStorage
            setUser(response.data.user);

            dispatch({
                type: 'LOAD_USER_SUCCESS',
                payload: response.data.user
            });
        } catch (error) {
            console.error('Erreur lors du chargement utilisateur:', error);
            // Ne pas nettoyer ni invalider immédiatement: laisser l'utilisateur connecté si le token n'est pas expiré
            dispatch({type: 'SET_LOADING', payload: false});
        }
    }, []);

    // Vérification initiale au chargement de l'application
    useEffect(() => {
        const token = getAuthToken();

        if (token) {
            // Si on a un token, vérifier la validité
            if (isTokenExpired(token)) {
                console.log('Token expiré au chargement, nettoyage...');
                clearAuth();
                dispatch({type: 'LOAD_USER_FAIL'});
            } else {
                // Token valide: restaurer depuis le cache local sans appeler l'API /auth/me
                const cached = getUser();
                if (cached) {
                    dispatch({ type: 'LOAD_USER_SUCCESS', payload: cached });
                } else {
                    dispatch({ type: 'SET_LOADING', payload: false });
                    dispatch({ type: 'SET_APP_LOADING', payload: false });
                }
            }
        } else {
            // Pas de token
            dispatch({type: 'LOAD_USER_FAIL'});
        }
    }, []); // Pas de dépendance sur loadUser pour éviter les boucles

    // Inscription
    const register = useCallback(async (userData) => {
        dispatch({type: 'REGISTER_START'});

        try {
            const response = await authAPI.register(userData);
            const data = response?.data || {};

            // Extraire le token depuis plusieurs formes possibles de réponse
            let rawToken = data.token || data.accessToken || data?.data?.token || data?.data?.accessToken || response?.headers?.authorization || response?.headers?.Authorization;
            if (rawToken) {
                setAuthToken(rawToken);
            }

            // Extraire l'utilisateur depuis plusieurs formes possibles
            const user = data.user || data?.data?.user || null;
            if (user) {
                setUser(user);
            }

            if (data.success || rawToken) {
                dispatch({
                    type: 'REGISTER_SUCCESS',
                    payload: {
                        user: user,
                        token: rawToken
                    }
                });
                return {success: true, message: data.message || 'Inscription réussie'};
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erreur lors de l\'inscription';
            dispatch({
                type: 'REGISTER_FAIL',
                payload: errorMessage
            });
            return {success: false, message: errorMessage};
        }
    }, []);

    // Connexion
    const login = useCallback(async (credentials) => {
        dispatch({type: 'LOGIN_START'});

        try {
            const response = await authAPI.login(credentials);
            const data = response?.data || {};

            // Extraire le token depuis plusieurs formes possibles de réponse
            let rawToken = data.token || data.accessToken || data?.data?.token || data?.data?.accessToken || response?.headers?.authorization || response?.headers?.Authorization;
            if (rawToken) {
                setAuthToken(rawToken);
            }

            // Extraire l'utilisateur depuis plusieurs formes possibles
            const user = data.user || data?.data?.user || null;
            if (user) {
                setUser(user);
            }

            if (data.success || rawToken) {
                dispatch({
                    type: 'LOGIN_SUCCESS',
                    payload: {
                        user: user,
                        token: rawToken
                    }
                });

                return {success: true, message: data.message || 'Connexion réussie'};
            }
        } catch (error) {
            // Normaliser le message d'erreur pour des identifiants invalides
            const status = error?.response?.status;
            let errorMessage;
            if (status === 401 || status === 400) {
                errorMessage = "Identifiants incorrects. Veuillez vérifier votre email ou mot de passe.";
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else {
                errorMessage = "Une erreur est survenue lors de la connexion. Veuillez réessayer.";
            }
            dispatch({
                type: 'LOGIN_FAIL',
                payload: errorMessage
            });
            return {success: false, message: errorMessage};
        }
    }, []);

    // Mettre à jour le profil (nom + email)
    const editProfile = useCallback(async ({ name, email }) => {
        const res = await userAPI.updateProfile({ name, email });
        const updated = res?.data?.user;
        if (updated) {
            setUser(updated);
            dispatch({ type: 'UPDATE_USER', payload: updated });
        }
        return updated;
    }, []);

    // Effacer les erreurs
    const clearError = useCallback(() => {
        dispatch({type: 'CLEAR_ERROR'});
    }, []);

    const value = {
        ...state,
        appLoading: state.appLoading,
        register,
        login,
        logout,
        clearError,
        loadUser,
        editProfile
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
